// Constants
import { ENV } from '../config/env';

const LOGO_API_KEY = ENV.VITE_LOGO_API_KEY;
const LOGO_FORMAT = 'PNG';
const LOGO_RETINA = true;

function getLogo(text: string, retina: boolean = LOGO_RETINA, format: string = LOGO_FORMAT) {
  // If no API key is configured or it's still the placeholder, return a fallback
  if (!LOGO_API_KEY || LOGO_API_KEY === 'your-logo-api-key-here') {
    // Return a simple favicon URL as fallback
    return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(text)}&sz=64`;
  }

  const logoUrl = `https://img.logo.dev/${encodeURIComponent(text)}?token=${LOGO_API_KEY}&format=${format}&retina=${retina}`;
  return logoUrl;
}

/**
 * Logo key type - ensures all logo keys are typed and prevents typos
 */
export type LogoKey =
  | 'ADOBE'
  | 'AI21'
  | 'ALIBABA'
  | 'AMAZON'
  | 'ANTHROPIC'
  | 'ALFRESCO'
  | 'APPIAN'
  | 'APPLE'
  | 'AWS'
  | 'AZURE'
  | 'CLAUDE'
  | 'CLIO'
  | 'COHERE'
  | 'CONFLUENCE'
  | 'DATABRICKS'
  | 'DATADOG'
  | 'DEEPSEEK'
  | 'BOX'
  | 'DROPBOX'
  | 'GCP'
  | 'GEMINI'
  | 'GENERIC_CARD'
  | 'GOOGLE'
  | 'GOOGLE_DRIVE'
  | 'GROQ'
  | 'HUBSPOT'
  | 'HUGGINGFACE'
  | 'IBM'
  | 'ICLOUD'
  | 'INFORMATICA'
  | 'LINK'
  | 'MASTERCARD'
  | 'META'
  | 'MICROSOFT'
  | 'MISTRAL'
  | 'MOSAIC'
  | 'NOTION'
  | 'NVIDIA'
  | 'ONBASE'
  | 'OPENAI'
  | 'OPENTEXT'
  | 'ORACLE'
  | 'PAYPAL'
  | 'PEGA'
  | 'PERPLEXITY'
  | 'QUICKBOOKS'
  | 'RUKA'
  | 'SALESFORCE'
  | 'SAP'
  | 'SERVICENOW'
  | 'SHOPIFY'
  | 'SIGNNOW'
  | 'SLACK'
  | 'SNOWFLAKE'
  | 'STABILITY'
  | 'STRIPE'
  | 'TALEND'
  | 'TOGETHER'
  | 'VISA'
  | 'XAI'
  | 'ZOHO';

/**
 * Type-safe logos object with typed keys
 * TypeScript will ensure all LogoKey values are present and no extra keys exist
 */
export const LOGOS: Record<LogoKey, string> = {
  ADOBE: getLogo('adobe.com'),
  AI21: getLogo('ai21.com'),
  ALIBABA: getLogo('alibaba.com'),
  AMAZON: getLogo('amazon.com'),
  ANTHROPIC: getLogo('anthropic.com'),
  ALFRESCO: '/marketplace/alfresco.svg', // Logo.dev doesn't support Alfresco
  APPIAN: getLogo('appian.com'),
  APPLE: getLogo('apple.com'),
  AWS: '/marketplace/aws.svg', // Logo.dev doesn't support AWS
  AZURE: '/marketplace/azure.svg', // Logo.dev doesn't support Azure
  CLAUDE: getLogo('claude.ai'),
  CLIO: getLogo('clio.com'),
  COHERE: getLogo('cohere.com'),
  CONFLUENCE: '/icons/confluence.svg',
  DATABRICKS: getLogo('databricks.com'),
  DATADOG: getLogo('datadog.com'),
  DEEPSEEK: getLogo('deepseek.com'),
  BOX: getLogo('box.com'),
  DROPBOX: getLogo('dropbox.com'),
  GCP: getLogo('googlecloudpresscorner.com'),
  GEMINI: '/icons/gemini.svg', // Logo.dev doesn't support Gemini as it's a sub-domain of Google
  GENERIC_CARD: '/icons/credit_card.svg',
  GOOGLE: getLogo('google.com'),
  GOOGLE_DRIVE: '/marketplace/google-drive.svg', // Use local SVG for accurate Google Drive logo
  GROQ: getLogo('groq.com'),
  HUBSPOT: getLogo('hubspot.com'),
  HUGGINGFACE: getLogo('huggingface.co'),
  IBM: getLogo('ibm.com'),
  ICLOUD: getLogo('icloud.com'),
  INFORMATICA: getLogo('informatica.com'),
  LINK: '/marketplace/link-by-stripe.png',
  MASTERCARD: getLogo('mastercard.com'),
  META: getLogo('meta.com'),
  MICROSOFT: getLogo('microsoft.com'),
  MISTRAL: getLogo('mistral.ai'),
  MOSAIC: getLogo('mosaicml.com'),
  NOTION: getLogo('notion.so'),
  NVIDIA: getLogo('nvidia.com'),
  ONBASE: getLogo('hyland.com'),
  OPENAI: getLogo('openai.com'),
  OPENTEXT: getLogo('opentext.com'),
  ORACLE: getLogo('oracle.com'),
  PAYPAL: getLogo('paypal.com'),
  PEGA: getLogo('pega.com'),
  PERPLEXITY: getLogo('perplexity.ai'),
  QUICKBOOKS: getLogo('quickbooks.intuit.com'),
  RUKA: getLogo('ruka.ai'),
  SALESFORCE: getLogo('salesforce.com'),
  SAP: getLogo('sap.com'),
  SERVICENOW: getLogo('service-now.com'),
  SHOPIFY: getLogo('shopify.com'),
  SIGNNOW: getLogo('signnow.com'),
  SLACK: getLogo('slack.com'),
  SNOWFLAKE: getLogo('snowflake.com'),
  STABILITY: getLogo('stability.ai'),
  STRIPE: getLogo('stripe.com'),
  TALEND: '/marketplace/talend.svg', // Logo.dev doesn't support Talend
  TOGETHER: getLogo('together.ai'),
  VISA: getLogo('visa.com'),
  XAI: getLogo('x.ai'),
  ZOHO: getLogo('zoho.com'),
};

export { getLogo };
